import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { parseApiError } from '../lib/parseApiError';
import { 
  Database, 
  Download, 
  Upload, 
  HardDrive, 
  AlertTriangle,
  CheckCircle,
  FileJson,
  Users,
  Building,
  Printer,
  Calendar,
  Ticket,
  FileText,
  Package,
  RefreshCw
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BackupPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [sovrascrivi, setSovrascrivi] = useState(false);
  const [importResult, setImportResult] = useState(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/backup/stats`);
      setStats(res.data);
    } catch (error) {
      toast.error('Errore nel caricamento statistiche');
    } finally {
      setLoading(false);
    }
  };

  const handleExportFull = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/backup/export?formato=json`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      link.setAttribute('download', `backup_crm_${today}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Backup scaricato con successo');
    } catch (error) {
      toast.error('Errore durante l\'export');
    } finally {
      setExporting(false);
    }
  };

  const handleExportCollection = async (collection) => {
    try {
      const response = await axios.get(`${API}/backup/export/${collection}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${collection}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`${collection} esportato`);
    } catch (error) {
      toast.error(`Errore export ${collection}`);
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.json')) {
      toast.error('Seleziona un file JSON');
      return;
    }

    if (sovrascrivi && !window.confirm(
      '⚠️ ATTENZIONE!\n\nHai selezionato "Sovrascrivi dati esistenti".\nQuesta operazione CANCELLERÀ tutti i dati attuali e li sostituirà con quelli del backup.\n\nSei sicuro di voler procedere?'
    )) {
      event.target.value = '';
      return;
    }

    setImporting(true);
    setImportResult(null);
    
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(
        `${API}/backup/import?sovrascrivi=${sovrascrivi}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setImportResult(res.data);
      toast.success('Import completato');
      fetchStats();
    } catch (error) {
      toast.error(parseApiError(error, 'Errore durante l\'import'));
    } finally {
      setImporting(false);
      event.target.value = '';
    }
  };

  const collections = [
    { key: 'clienti', label: 'Clienti', icon: Users, color: 'text-blue-500' },
    { key: 'punti_vendita', label: 'Punti Vendita', icon: Building, color: 'text-indigo-500' },
    { key: 'impianti', label: 'Impianti', icon: Printer, color: 'text-purple-500' },
    { key: 'abbonamenti', label: 'Abbonamenti', icon: Calendar, color: 'text-green-500' },
    { key: 'tickets', label: 'Tickets', icon: Ticket, color: 'text-orange-500' },
    { key: 'interventi', label: 'Interventi', icon: RefreshCw, color: 'text-cyan-500' },
    { key: 'documenti', label: 'Documenti', icon: FileText, color: 'text-pink-500' },
    { key: 'articoli', label: 'Articoli Magazzino', icon: Package, color: 'text-amber-500' },
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="backup-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-black tracking-tight flex items-center gap-2">
          <Database className="h-8 w-8" />
          Backup & Ripristino
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Esporta e importa tutti i dati del CRM per sicurezza
        </p>
      </div>

      {/* Statistiche Database */}
      <Card className="rounded-none border-l-4 border-l-blue-500">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-mono uppercase tracking-wider flex items-center gap-2">
            <HardDrive className="h-4 w-4" />
            Stato Database
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="animate-pulse">Caricamento...</div>
          ) : stats ? (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {collections.map(({ key, label, icon: Icon, color }) => (
                <div key={key} className="flex items-center gap-2">
                  <Icon className={`h-5 w-5 ${color}`} />
                  <div>
                    <p className="text-xl font-black">{stats[key] || 0}</p>
                    <p className="text-xs text-muted-foreground">{label}</p>
                  </div>
                </div>
              ))}
              <div className="flex items-center gap-2 col-span-2">
                <Database className="h-5 w-5 text-primary" />
                <div>
                  <p className="text-xl font-black text-primary">{stats.totale_record || 0}</p>
                  <p className="text-xs text-muted-foreground">Totale Record</p>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-muted-foreground">Impossibile caricare statistiche</p>
          )}
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Export */}
        <Card className="rounded-none">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="h-5 w-5 text-green-500" />
              Esporta Backup
            </CardTitle>
            <CardDescription>
              Scarica una copia completa di tutti i dati
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button 
              onClick={handleExportFull}
              disabled={exporting}
              className="w-full rounded-none font-mono uppercase"
              size="lg"
              data-testid="export-full-btn"
            >
              <FileJson className="h-5 w-5 mr-2" />
              {exporting ? 'Esportazione...' : 'Scarica Backup Completo (JSON)'}
            </Button>
            
            <div className="border-t pt-4">
              <p className="text-sm font-medium mb-3">Esporta singole tabelle (CSV):</p>
              <div className="grid grid-cols-2 gap-2">
                {collections.map(({ key, label, icon: Icon }) => (
                  <Button
                    key={key}
                    variant="outline"
                    size="sm"
                    className="rounded-none justify-start"
                    onClick={() => handleExportCollection(key)}
                  >
                    <Icon className="h-4 w-4 mr-2" />
                    {label}
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Import */}
        <Card className="rounded-none">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-blue-500" />
              Importa Backup
            </CardTitle>
            <CardDescription>
              Ripristina i dati da un file di backup
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-none">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-amber-800 dark:text-amber-200">Attenzione</p>
                  <p className="text-amber-700 dark:text-amber-300">
                    L'import aggiungerà i nuovi record senza cancellare quelli esistenti,
                    a meno che non selezioni "Sovrascrivi".
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-none">
              <Checkbox 
                id="sovrascrivi"
                checked={sovrascrivi}
                onCheckedChange={setSovrascrivi}
              />
              <Label htmlFor="sovrascrivi" className="text-sm text-red-700 dark:text-red-300">
                <strong>Sovrascrivi dati esistenti</strong> (cancella tutto prima dell'import)
              </Label>
            </div>

            <div>
              <Label className="text-xs font-mono uppercase">Seleziona file backup (JSON)</Label>
              <input
                type="file"
                accept=".json"
                onChange={handleImport}
                disabled={importing}
                className="mt-2 w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-none file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 cursor-pointer"
                data-testid="import-file-input"
              />
            </div>

            {importing && (
              <div className="flex items-center gap-2 text-blue-600">
                <RefreshCw className="h-4 w-4 animate-spin" />
                <span>Importazione in corso...</span>
              </div>
            )}

            {importResult && (
              <div className="p-4 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-none">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span className="font-medium text-green-800 dark:text-green-200">Import completato</span>
                </div>
                <div className="text-sm text-green-700 dark:text-green-300">
                  <p className="font-medium">Record importati:</p>
                  <ul className="mt-1 space-y-1">
                    {Object.entries(importResult.details?.importati || {}).map(([key, value]) => (
                      <li key={key}>• {key}: {value}</li>
                    ))}
                  </ul>
                  {Object.keys(importResult.details?.errori || {}).length > 0 && (
                    <>
                      <p className="font-medium mt-2 text-red-600">Errori:</p>
                      <ul className="mt-1">
                        {Object.entries(importResult.details.errori).map(([key, value]) => (
                          <li key={key} className="text-red-600">• {key}: {value}</li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Consigli */}
      <Card className="rounded-none bg-muted/30">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-mono uppercase tracking-wider">
            💡 Consigli per la Sicurezza dei Dati
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm">
            <li className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
              <span><strong>Backup settimanale:</strong> Scarica un backup completo almeno una volta a settimana</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
              <span><strong>Conserva più versioni:</strong> Mantieni gli ultimi 4-5 backup in cartelle diverse</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
              <span><strong>Cloud storage:</strong> Salva i backup su Google Drive, Dropbox o simili</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
              <span><strong>Verifica i backup:</strong> Ogni tanto prova a importare un backup su un ambiente di test</span>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
